import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton
} from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const ForcePasswordChange: React.FC = () => {
  const { user } = useAuth(); // Get user from context
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Slaptažodžiai nesutampa');
      setLoading(false);
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setError('Naujas slaptažodis turi būti bent 8 simbolių ilgio');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const requestData = {
        newPassword,
        confirmNewPassword: confirmPassword
      };
      
      // SECURITY: Do not log password data
      console.log('Sending password change request (password data excluded from logs)');
      console.log('Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.post(
        getApiUrl(API_ENDPOINTS.FORCE_CHANGE_PASSWORD),
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.removeItem('passwordResetRequired');
        setSuccess(true);
        
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      }
    } catch (error: any) {
      console.error('Password change error:', error);
      console.error('Error response:', error.response?.data);
      
      // Show specific validation errors if available
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        setError(`Validation error: ${errorMessages}`);
      } else {
        setError(error.response?.data?.error || 'Nepavyko pakeisti slaptažodžio');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all auth-related data
    localStorage.removeItem('token');
    localStorage.removeItem('passwordResetRequired');
    
    // Redirect to login page
    window.location.href = '/';
  };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'success.main',
                borderRadius: '50%',
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <LockOutlined sx={{ color: 'white', fontSize: 28 }} />
            </Box>

            <Typography component="h1" variant="h4" gutterBottom>
              Slaptažodis sėkmingai pakeistas
            </Typography>
            
            <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
              <Typography variant="body1">
                Jūsų slaptažodis buvo sėkmingai pakeistas. Būsite nukreipti į pagrindinį puslapį.
              </Typography>
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'warning.main',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 2,
            }}
          >
            <LockOutlined sx={{ color: 'white', fontSize: 28 }} />
          </Box>

          <Typography component="h1" variant="h4" gutterBottom>
            Privalomas slaptažodžio keitimas
          </Typography>
          
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Administratorius atstatė jūsų slaptažodį
          </Typography>

          <Alert severity="warning" sx={{ width: '100%', mb: 3 }}>
            <Typography variant="body2">
              Jūsų slaptažodis buvo atstatas administratoriaus. Privalote pakeisti jį šiandien.
            </Typography>
          </Alert>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Alert severity="info" sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2">
                Pamiršote laikiną slaptažodį? Atsijunkite ir susisiekite su administratoriumi naujam slaptažodžio atstatymui.
              </Typography>
            </Alert>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Naujas slaptažodis"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              helperText="Minimalus ilgis: 8 simboliai"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowNewPassword(prev => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Patvirtinti naują slaptažodį"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Slaptažodžiai nesutampa' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', width: '100%', mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleLogout}
                sx={{ py: 1.5 }}
              >
                Atsijungti
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                sx={{ py: 1.5, flex: 1 }}
                disabled={loading || newPassword !== confirmPassword || newPassword.length < 8}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Pakeisti slaptažodį'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForcePasswordChange;
